import { FALLOUT_REASONS, TESTIMONIAL_ATTRIBUTIONS, type FalloutReason, type SessionUser, type TestimonialAttribution } from '@motoveo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { OperatingGuardrails } from '../components/OperatingGuardrails';
import { dollarsToCents, formatCents, formatDate } from '../lib/format';

const CLOSED = new Set(['closed_won', 'closed_lost', 'fell_through']);

/**
 * One deal: status pipeline, the explicit close-out step (won → realized-sale comp; lost → structured
 * fallout reason), seller/dealer confirmation + testimonial consent (M12 close attribution), NPS
 * feedback capture (both sides), and the audited disclosure timeline. Because Motoveo never touches
 * money/title, this screen is the ONLY place a deal's real outcome — win OR loss — becomes data.
 */
export function DealDetailPage({ user }: { user: SessionUser }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [price, setPrice] = useState('');
  const [fallout, setFallout] = useState<FalloutReason | ''>('');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [tPerm, setTPerm] = useState(false);
  const [tAttr, setTAttr] = useState<TestimonialAttribution>('attributed');
  const [tQuote, setTQuote] = useState('');
  const [sellerNps, setSellerNps] = useState('');
  const [sellerComment, setSellerComment] = useState('');
  const [dealerNps, setDealerNps] = useState('');
  const [dealerComment, setDealerComment] = useState('');

  const deal = useQuery({ queryKey: ['deal', id], queryFn: () => api.deals.get(id), enabled: Boolean(id) });
  const invalidate = () => qc.invalidateQueries();

  const transition = useMutation({ mutationFn: (status: 'intro_made' | 'in_progress') => api.deals.transition(id, { status }), onSuccess: invalidate });
  const closeWon = useMutation({
    mutationFn: (cents: number) => api.deals.close(id, { status: 'closed_won', outcome: 'sold', closed_price_cents: cents }),
    onSuccess: invalidate,
  });
  const closeLost = useMutation({
    mutationFn: (falloutReason: FalloutReason | null) =>
      api.deals.close(id, { status: 'closed_lost', outcome: 'no_sale', ...(falloutReason ? { fallout_reason: falloutReason } : {}) }),
    onSuccess: invalidate,
  });
  const closeOut = useMutation({ mutationFn: (body: Parameters<typeof api.deals.closeOut>[1]) => api.deals.closeOut(id, body), onSuccess: invalidate });
  const feedback = useMutation({ mutationFn: (body: Parameters<typeof api.deals.addFeedback>[1]) => api.deals.addFeedback(id, body), onSuccess: invalidate });
  // M15 — the founder correction for a WRONG closed_won price: audit-void the realized comp so it
  // stops feeding the oracle/gate/valuation (reason required; the row is kept, never deleted).
  const voidComp = useMutation({
    mutationFn: ({ compId, reason }: { compId: string; reason: string }) => api.valuation.voidComp(compId, reason),
    onSuccess: invalidate,
  });
  const promptVoidComp = (compId: string) => {
    const reason = window.prompt(
      'Void this realized comp? It stops feeding the pricing oracle / launch gate (audited, not deleted).\n\nReason (required):',
    );
    if (reason && reason.trim().length >= 3) voidComp.mutate({ compId, reason: reason.trim() });
  };

  return (
    <Layout user={user}>
      {deal.isLoading ? (
        <p className="muted">Loading…</p>
      ) : deal.isError ? (
        <p className="error">{(deal.error as Error).message}</p>
      ) : deal.data ? (
        (() => {
          const d = deal.data;
          const closed = CLOSED.has(d.deal.status);
          const failed = d.deal.status === 'closed_lost' || d.deal.status === 'fell_through';
          return (
            <>
              <p>
                <Link to="/deals" className="muted">
                  ← Deals
                </Link>
              </p>
              <div className="row-between">
                <h2>
                  {d.model_designation ?? 'Deal'} <span className="pill">{d.deal.status}</span>
                </h2>
                <span className="muted">{d.deal.tier_context}</span>
              </div>

              <OperatingGuardrails variant="deal" />

              <div className="card">
                <section className="grid-2">
                  <div className="field">
                    <span className="field-label">Dealer</span>
                    <span className="field-value">{d.dealer_business_name}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">Submission</span>
                    <span className="field-value">
                      <Link to={`/submissions/${d.deal.seller_submission_id}`}>{d.public_ref ?? d.deal.seller_submission_id.slice(0, 8)}</Link>
                    </span>
                  </div>
                  <div className="field">
                    <span className="field-label">Agreed price</span>
                    <span className="field-value">{formatCents(d.deal.agreed_price_cents)}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">Realized price</span>
                    <span className="field-value">{formatCents(d.deal.closed_price_cents)}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">Outcome</span>
                    <span className="field-value">{d.deal.outcome ?? '—'}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">Fallout reason</span>
                    <span className="field-value">{d.deal.fallout_reason ? d.deal.fallout_reason.replace(/_/g, ' ') : '—'}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">Confirmed</span>
                    <span className="field-value">
                      seller {d.deal.seller_confirmed ? '✓' : '—'} · dealer {d.deal.dealer_confirmed ? '✓' : '—'}
                    </span>
                  </div>
                  <div className="field">
                    <span className="field-label">Realized comp</span>
                    <span className="field-value">
                      {d.realized_comp_id ? (
                        <>
                          <code>{d.realized_comp_id.slice(0, 8)}…</code>{' '}
                          {d.realized_comp_voided ? (
                            <span className="pill">voided</span>
                          ) : (
                            <button
                              type="button"
                              className="plain"
                              title="Wrong close price? Audit-void this comp so it stops feeding the pricing oracle (reason required)"
                              disabled={voidComp.isPending}
                              onClick={() => promptVoidComp(d.realized_comp_id!)}
                            >
                              void
                            </button>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                </section>
                {voidComp.isError ? <p className="error">{(voidComp.error as Error).message}</p> : null}
              </div>

              {/* pipeline + close controls */}
              {!closed ? (
                <div className="card">
                  <h3>Advance / close</h3>
                  <div className="inline" style={{ flexWrap: 'wrap', gap: 8 }}>
                    <button className="ghost" onClick={() => transition.mutate('intro_made')} disabled={transition.isPending || d.deal.status === 'intro_made'}>
                      Mark intro made
                    </button>
                    <button className="ghost" onClick={() => transition.mutate('in_progress')} disabled={transition.isPending || d.deal.status === 'in_progress'}>
                      Mark in progress
                    </button>
                    <span style={{ flex: 1 }} />
                    <input placeholder="realized $ (e.g. 87,000)" value={price} onChange={(e) => setPrice(e.target.value)} />
                    <button
                      onClick={() => {
                        const c = dollarsToCents(price);
                        if (c != null) closeWon.mutate(c);
                      }}
                      disabled={closeWon.isPending || dollarsToCents(price) == null}
                    >
                      Close WON (writes a comp)
                    </button>
                  </div>
                  <div className="inline" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    <select value={fallout} onChange={(e) => setFallout(e.target.value as FalloutReason | '')}>
                      <option value="">fallout reason…</option>
                      {FALLOUT_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                    <button className="ghost" onClick={() => closeLost.mutate(fallout || null)} disabled={closeLost.isPending}>
                      Close LOST (records the fallout reason)
                    </button>
                  </div>
                  {closeWon.isError ? <p className="error">{(closeWon.error as Error).message}</p> : null}
                  {closeLost.isError ? <p className="error">{(closeLost.error as Error).message}</p> : null}
                </div>
              ) : (
                <div className="card">
                  <p className="muted">
                    Closed {d.deal.closed_at ? formatDate(d.deal.closed_at) : ''} · {d.deal.outcome ?? ''}
                    {d.deal.fallout_reason ? ` · ${d.deal.fallout_reason.replace(/_/g, ' ')}` : ''}
                    {d.realized_comp_id ? ' · a realized_sale comp was written to the oracle.' : ''}
                  </p>
                </div>
              )}

              {/* M12 close-out attribution — works whether the deal is open or already closed. A FAILED
                  deal still yields structured learning here; a WIN can become a marketing asset w/ consent. */}
              <div className="card">
                <h3>Close-out &amp; attribution</h3>
                <p className="hint">Motoveo stays out of funds/title — record what the parties reported. Both sides.</p>
                <div className="inline" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <button
                    className="ghost"
                    onClick={() => closeOut.mutate({ seller_confirmed: !d.deal.seller_confirmed })}
                    disabled={closeOut.isPending}
                  >
                    {d.deal.seller_confirmed ? 'Seller confirmed ✓ (undo)' : 'Mark seller-confirmed'}
                  </button>
                  <button
                    className="ghost"
                    onClick={() => closeOut.mutate({ dealer_confirmed: !d.deal.dealer_confirmed })}
                    disabled={closeOut.isPending}
                  >
                    {d.deal.dealer_confirmed ? 'Dealer confirmed ✓ (undo)' : 'Mark dealer-confirmed'}
                  </button>
                </div>

                {failed ? (
                  <div className="inline" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    <select value={fallout} onChange={(e) => setFallout(e.target.value as FalloutReason | '')}>
                      <option value="">fallout reason…</option>
                      {FALLOUT_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                    <input placeholder="note (free text)" value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} />
                    <button
                      className="ghost"
                      onClick={() =>
                        closeOut.mutate({ ...(fallout ? { fallout_reason: fallout } : {}), ...(outcomeNote ? { outcome_reason: outcomeNote } : {}) })
                      }
                      disabled={closeOut.isPending || (!fallout && !outcomeNote)}
                    >
                      Save fallout learning
                    </button>
                  </div>
                ) : null}

                <div style={{ marginTop: 12 }}>
                  <label className="inline">
                    <input type="checkbox" checked={tPerm} onChange={(e) => setTPerm(e.target.checked)} /> testimonial / case-study permission
                  </label>
                  <div className="inline" style={{ flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    <select value={tAttr} onChange={(e) => setTAttr(e.target.value as TestimonialAttribution)} disabled={!tPerm}>
                      {TESTIMONIAL_ATTRIBUTIONS.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                    <input style={{ flex: 1, minWidth: 220 }} placeholder="quote (optional)" value={tQuote} onChange={(e) => setTQuote(e.target.value)} disabled={!tPerm} />
                    <button
                      className="ghost"
                      onClick={() => closeOut.mutate({ testimonial_permission: tPerm, testimonial_attribution: tAttr, testimonial_quote: tQuote || null })}
                      disabled={closeOut.isPending}
                    >
                      Save testimonial consent
                    </button>
                  </div>
                  {d.deal.testimonial_permission ? (
                    <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Consent on file ({d.deal.testimonial_attribution ?? 'attributed'}){d.deal.testimonial_quote ? `: “${d.deal.testimonial_quote}”` : ''}
                    </p>
                  ) : null}
                </div>
                {closeOut.isError ? <p className="error">{(closeOut.error as Error).message}</p> : null}
              </div>

              {/* M12 NPS feedback — both sides. Turns a close (win OR loss) into a relationship signal. */}
              <div className="card">
                <h3>Feedback (NPS)</h3>
                <div className="grid-2">
                  <div>
                    <strong>Seller</strong>
                    <div className="inline" style={{ gap: 8, marginTop: 6 }}>
                      <input type="number" min={0} max={10} placeholder="0–10" style={{ width: 80 }} value={sellerNps} onChange={(e) => setSellerNps(e.target.value)} />
                      <input style={{ flex: 1 }} placeholder="comment" value={sellerComment} onChange={(e) => setSellerComment(e.target.value)} />
                      <button
                        className="ghost"
                        onClick={() => feedback.mutate({ party: 'seller', nps: sellerNps === '' ? null : Number(sellerNps), comment: sellerComment || null })}
                        disabled={feedback.isPending || (sellerNps === '' && !sellerComment)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div>
                    <strong>Dealer</strong>
                    <div className="inline" style={{ gap: 8, marginTop: 6 }}>
                      <input type="number" min={0} max={10} placeholder="0–10" style={{ width: 80 }} value={dealerNps} onChange={(e) => setDealerNps(e.target.value)} />
                      <input style={{ flex: 1 }} placeholder="comment" value={dealerComment} onChange={(e) => setDealerComment(e.target.value)} />
                      <button
                        className="ghost"
                        onClick={() => feedback.mutate({ party: 'dealer', nps: dealerNps === '' ? null : Number(dealerNps), comment: dealerComment || null })}
                        disabled={feedback.isPending || (dealerNps === '' && !dealerComment)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
                {d.feedback.length > 0 ? (
                  <ul style={{ marginTop: 12 }}>
                    {d.feedback.map((f) => (
                      <li key={f.id}>
                        <strong>{f.party}</strong>: NPS {f.nps ?? '—'}
                        {f.comment ? ` — “${f.comment}”` : ''} <span className="muted">· {formatDate(f.collected_at)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted" style={{ marginTop: 8 }}>
                    No feedback captured yet.
                  </p>
                )}
                {feedback.isError ? <p className="error">{(feedback.error as Error).message}</p> : null}
              </div>

              {/* winning offer + bid */}
              <div className="card">
                <h3>Winning offer &amp; bid</h3>
                <p>
                  Offer <strong>{formatCents(d.winning_offer?.presented_amount_cents ?? null)}</strong>{' '}
                  <span className="muted">
                    (bid {formatCents(d.winning_bid?.amount_cents ?? null)} · {d.winning_bid?.bid_type ?? '—'})
                  </span>
                </p>
              </div>

              {/* the audited staged-disclosure timeline */}
              <div className="card">
                <h3>Disclosure timeline</h3>
                {d.disclosure_timeline.length === 0 ? (
                  <p className="muted">No reveals recorded.</p>
                ) : (
                  <ul>
                    {d.disclosure_timeline.map((e) => (
                      <li key={e.id}>
                        <strong>{e.reveal_type.replace(/_/g, ' ')}</strong> — {e.trigger_reason} <span className="muted">· {formatDate(e.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          );
        })()
      ) : null}
    </Layout>
  );
}
